import { PowerSyncDatabase, Table, column } from "@powersync/web";
import { useQuery, useStatus } from "@powersync/react";
import { TABLES, psSchema, syncConnector, type TableName } from "@cliqsuite/db/powersync";
import { bindCollections } from "@cliqsuite/db";

// Every table the business buckets can send an owner (powersync/sync-rules.yaml); a table the
// token doesn't grant just stays empty. staff_pay carries base_salary, for people with Payroll.
const schema = psSchema(Object.keys(TABLES) as TableName[], { staff_pay: new Table({ base_salary: column.integer }) });

// Owners keep several tabs open: the default multi-tab mode shares one sync connection between them.
export const db = new PowerSyncDatabase({ schema, database: { dbFilename: "owner.db" } });
bindCollections(db);

const IDENTITY_KEY = "cliqsuite-owner-db-identity";

/** Starts syncing for this login. A different account on the same browser starts from an empty database. */
export async function connectOwnerDb(identity: string): Promise<void> {
  let previous: string | null = null;
  try {
    previous = localStorage.getItem(IDENTITY_KEY);
    localStorage.setItem(IDENTITY_KEY, identity);
  } catch {
    // Storage blocked: can't tell accounts apart, so never keep data between sessions.
    previous = null;
  }
  if (previous !== identity) await db.disconnectAndClear();
  await db.connect(syncConnector());
}

/** Logout: drop everything this account synced. */
export async function clearOwnerDb(): Promise<void> {
  try {
    localStorage.removeItem(IDENTITY_KEY);
  } catch {
    // ignore
  }
  await db.disconnectAndClear();
}

/** True once the first full sync has landed, so "no data" never flashes before data arrives. */
export function useOwnerSyncReady(): boolean {
  return useStatus().hasSynced ?? false;
}

/** A raw SQL read against the local database: `undefined` until the first result, then `{ rows }` (the old PGlite hook's shape). */
export function useSql<T>(sql: string, params: unknown[] = []): { rows: T[] } | undefined {
  const { data, isLoading } = useQuery<T>(sql, params);
  return isLoading ? undefined : { rows: data };
}
